import React from "react";

/* ── Age helpers ──────────────────────────────────────────────── */

/** Compute integer years from a date-of-birth string (ISO or locale). Returns null if input is falsy. */
export function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/** Days remaining until a grace deadline. Returns null if input is falsy. */
export function daysUntil(deadline: string | null | undefined): number | null {
  if (!deadline) return null;
  const d = new Date(deadline);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return Math.ceil((d.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
}

/* ── Age-band chip (matches mobile app colors) ───────────────── */

type AgeBand = "child" | "teen" | "adult" | "senior" | "unspecified";

const AGE_BAND_STYLES: Record<AgeBand, { bg: string; text: string }> = {
  child: { bg: "#fef3c7", text: "#92400e" },
  teen: { bg: "#dbeafe", text: "#1e40af" },
  senior: { bg: "#ede9fe", text: "#5b21b6" },
  adult: { bg: "#e5e7eb", text: "#374151" },
  unspecified: { bg: "#e5e7eb", text: "#374151" },
};

const AGE_BAND_LABELS: Record<AgeBand, string> = {
  child: "Child",
  teen: "Teen",
  adult: "Adult",
  senior: "Senior",
  unspecified: "Unspecified",
};

export function AgeBandChip({ band }: { band: AgeBand | string }) {
  const key = (band as AgeBand) in AGE_BAND_STYLES ? (band as AgeBand) : "unspecified";
  const style = AGE_BAND_STYLES[key];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {AGE_BAND_LABELS[key]}
    </span>
  );
}

/* ── DOB grace badge (amber "Awaiting DOB" + countdown) ──────── */

export function DobGraceBadge({
  graceUntil,
  compact = false,
}: {
  graceUntil: string | null | undefined;
  compact?: boolean;
}) {
  const days = daysUntil(graceUntil);
  const expired = days !== null && days <= 0;

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/20">
        Awaiting DOB
      </span>
      {!compact && graceUntil && (
        <span className={`text-[10px] leading-tight ${expired ? "text-red-400" : "text-gray-400"}`}>
          {expired ? "Grace expired" : `${days} day${days === 1 ? "" : "s"} left`}
        </span>
      )}
    </span>
  );
}

/* ── "Booking for" chip with age-band pill ───────────────────── */

export function BookingForChip({
  name,
  ageBand,
  notes,
}: {
  name: string | null | undefined;
  ageBand: string | null | undefined;
  notes?: string | null | undefined;
}) {
  if (!name) return null;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-800 text-gray-200 border border-gray-700">
          <span className="text-gray-400">Booking for:</span>
          <span className="font-semibold">{name}</span>
        </span>
        {ageBand && <AgeBandChip band={ageBand} />}
      </div>
      {notes && (
        <p className="text-xs italic text-gray-400 ml-1">{notes}</p>
      )}
    </div>
  );
}
